// no-dd-sa:typescript-code-style/max-params
import { logError, logInfo } from '@/lib/logger';
/**
 * Unified Analytics Module
 *
 * Single interface for GA4, Microsoft Clarity, and Google Ads tracking.
 * All tracking is disabled in non-production environments unless debug mode is enabled.
 *
 * Usage:
 *   import { analytics } from '@/lib/analytics';
 *
 *   // Initialize (call once in App.tsx)
 *   analytics.init();
 *
 *   // Identify user after login
 *   analytics.identify({ userId: '123', ... });
 *
 *   // Track events
 *   analytics.track({ name: 'feature_used', params: { feature: 'dashboard' } });
 *
 *   // Track conversions
 *   analytics.trackConversion('PURCHASE', 199);
 *
 *   // Track page views
 *   analytics.page('/dashboard', 'Dashboard');
 */

import * as ga4 from './ga4';
import * as clarity from './clarity';
import * as googleAds from './googleAds';
import * as linkedin from './linkedin';
import * as posthog from './posthog';
import { setSentryUser, updateSentryOrgContext, clearSentryUser } from '@/lib/sentry';
import { GA4_ID, CLARITY_ID, GOOGLE_ADS_ID, LINKEDIN_PARTNER_ID, POSTHOG_API_KEY, CONVERSIONS, CONVERSION_GA4_EVENT } from './config';
import { captureFirstLandingPage } from './firstTouch';
import type { AnalyticsUser, TrackingEvent, EcommerceItem } from './types';

const IS_PRODUCTION = import.meta.env.PROD;
const DEBUG_MODE = typeof window !== 'undefined' && localStorage.getItem('analytics_debug') === 'true';

class Analytics {
  private initialized = false;
  private user: AnalyticsUser | null = null;
  // A pageview attempted before init() completed, replayed once init() runs.
  private pendingPage: { path: string; title: string } | null = null;

  /**
   * Initialize all analytics providers
   * Call this once in App.tsx
   */
  init(): void {
    if (this.initialized) return;

    if (!IS_PRODUCTION && !DEBUG_MODE) {
      logInfo('[Analytics] Disabled in non-production environment. Set localStorage.analytics_debug="true" to enable.');
      return;
    }

    ga4.init(GA4_ID);
    clarity.init(CLARITY_ID);

    // Only init Google Ads if configured (not placeholder)
    if (!GOOGLE_ADS_ID.includes('XXXXXXXXX')) {
      googleAds.init(GOOGLE_ADS_ID);
    }

    // Init LinkedIn Insight Tag if configured
    if (LINKEDIN_PARTNER_ID) {
      linkedin.init(LINKEDIN_PARTNER_ID);
    }

    // Init PostHog if API key is configured
    if (POSTHOG_API_KEY) {
      posthog.init();
    }

    this.initialized = true;
    this.log('Initialized');

    // Replay a pageview that was attempted before init() completed (e.g. the
    // ad-landing pageview), so it isn't lost to the init race.
    if (this.pendingPage) {
      const { path, title } = this.pendingPage;
      this.pendingPage = null;
      this.page(path, title);
    }
  }

  /**
   * Identify user across all providers
   * Call after successful login
   */
  async identify(user: AnalyticsUser): Promise<void> {
    if (!user.userId) {
      // A falsy distinct id mints an anonymous PostHog person and logs
      // "[PostHog.js] Unique user id has not been set". Keep the previous
      // identity for the analytics providers, but Sentry user context is
      // ALWAYS set (error tracking, not analytics) - errors from exactly the
      // cohort hitting this broken payload must stay attributable.
      setSentryUser(user);
      logError('analytics.identify called without userId', JSON.stringify(user));
      return;
    }
    this.user = user;

    // Sentry is error tracking, not analytics — always set user context
    setSentryUser(user);

    if (!this.shouldTrack()) {
      this.log('Identify:', user);
      return;
    }

    ga4.identify(user);
    clarity.identify(user);
    posthog.identify(user);

    // Set enhanced conversion data for Google Ads
    if (user.email) {
      await googleAds.setUserData(user);
    }

    this.log('Identified user:', user.userId);
  }

  /**
   * Update user properties (e.g., when org changes)
   */
  updateUser(updates: Partial<AnalyticsUser>): void {
    if (!this.user) return;

    this.user = { ...this.user, ...updates };

    // Sentry is error tracking — always update org context
    updateSentryOrgContext(this.user);

    if (!this.shouldTrack()) {
      this.log('Update user:', updates);
      return;
    }

    ga4.identify(this.user);
    clarity.identify(this.user);
    posthog.register({
      org_id: this.user.orgId,
      org_slug: this.user.orgSlug,
      org_name: this.user.orgName,
      is_personal: this.user.isPersonal,
      subscription_tier: this.user.subscriptionTier,
    });
  }

  /**
   * Track a custom event
   */
  track(event: TrackingEvent): void {
    if (!this.shouldTrack()) {
      this.log('Track:', event);
      return;
    }

    // Add user context to all events
    const enrichedParams = {
      ...event.params,
      subscription_tier: this.user?.subscriptionTier,
      org_id: this.user?.orgId,
    };

    ga4.trackEvent(event.name, enrichedParams);

    // Also track to Clarity as tags for session filtering
    clarity.trackEvent(event.name, this.paramsToStrings(event.params));

    // Track to PostHog for funnels and experiments
    posthog.track(event.name, enrichedParams);

    this.log('Track:', event.name, enrichedParams);
  }

  /**
   * Track page view
   */
  page(path: string, title: string): void {
    // Record the first landing page before any guard - it must be captured even
    // for the anonymous, pre-init landing pageview (identify() reads it back
    // later via a user property). Capture-once + localStorage-backed.
    captureFirstLandingPage();

    if (!this.shouldTrack()) {
      // The landing pageview often fires before init() completes: the child
      // <PageTracker> effect runs before App's parent init effect. Without
      // buffering it, the very first (ad-entry) pageview is silently dropped
      // while PostHog's own $web_vitals/$pageleave still fire and carry the
      // gclid - leaving paid traffic under-recorded. Buffer it and flush on
      // init(). Skip when tracking is deliberately off (superadmin).
      if (!this.initialized && !this.user?.isSuperadmin) {
        this.pendingPage = { path, title };
      }
      this.log('Page:', { path, title });
      return;
    }

    ga4.trackPageView(path, title, {
      subscription_tier: this.user?.subscriptionTier,
      org_id: this.user?.orgId,
    });

    // Track to PostHog for funnel analysis
    posthog.page(path, title);

    this.log('Page:', path);
  }

  /**
   * Track conversion (Google Ads + GA4 + PostHog)
   */
  trackConversion(
    type: keyof typeof CONVERSIONS,
    value?: number,
    currency?: string,
    transactionId?: string,
    options?: { skipPostHog?: boolean }
  ): void {
    const config = CONVERSIONS[type];

    if (!this.shouldTrack()) {
      this.log('Conversion:', { type, value, currency, transactionId });
      return;
    }

    // Skip if Google Ads not configured
    if (!GOOGLE_ADS_ID.includes('XXXXXXXXX')) {
      googleAds.trackConversion(config, value, currency || 'USD', transactionId);
    }

    // Emit a distinctly-named GA4 event (trial_started, first_repo_imported, …)
    // so GA4 can build funnel steps and key events from it. Types that already
    // fire a first-class GA4 event elsewhere (sign_up, purchase) map to null in
    // CONVERSION_GA4_EVENT and are skipped here to avoid double-counting.
    const ga4EventName = CONVERSION_GA4_EVENT[type];
    if (ga4EventName) {
      ga4.trackEvent(ga4EventName, {
        value,
        currency: currency || 'USD',
        transaction_id: transactionId,
      });
    }

    // PostHog: emit under the funnel event name (TRIAL_STARTED -> trial_started)
    // so conversions appear in PostHog funnels. trackPurchase() opts out because
    // it already sends a richer posthog purchase event itself.
    if (!options?.skipPostHog) {
      posthog.track(type.toLowerCase(), {
        value,
        currency: currency || 'USD',
        transaction_id: transactionId,
        subscription_tier: this.user?.subscriptionTier,
        org_id: this.user?.orgId,
      });
    }

    this.log('Conversion:', type, value);
  }

  /**
   * Track ecommerce begin_checkout (GA4 recommended event)
   */
  trackBeginCheckout(
    tier: 'pro' | 'business',
    billingCycle: 'monthly' | 'yearly',
    value: number
  ): void {
    if (!this.shouldTrack()) {
      this.log('Begin Checkout:', { tier, billingCycle, value });
      return;
    }

    const items: EcommerceItem[] = [{
      item_id: `${tier}_${billingCycle}`,
      item_name: `CodePulse ${tier.charAt(0).toUpperCase() + tier.slice(1)} (${billingCycle})`,
      price: value,
      quantity: 1,
    }];

    ga4.trackBeginCheckout(value, 'USD', items);

    this.log('Begin Checkout:', { tier, value });
  }

  /**
   * Track ecommerce purchase (GA4 recommended event)
   */
  trackPurchase(
    tier: 'pro' | 'business',
    billingCycle: 'monthly' | 'yearly',
    value: number,
    currency: string,
    transactionId: string
  ): void {
    if (!this.shouldTrack()) {
      this.log('Purchase:', { tier, billingCycle, value, currency, transactionId });
      return;
    }

    const items: EcommerceItem[] = [{
      item_id: `${tier}_${billingCycle}`,
      item_name: `CodePulse ${tier.charAt(0).toUpperCase() + tier.slice(1)} (${billingCycle})`,
      price: value,
      quantity: 1,
    }];

    ga4.trackPurchase(transactionId, value, currency, items);

    // NO client-side PostHog purchase event: the server-side capture in
    // analytics_service.py is the single source of truth (it fires even if
    // the buyer never returns to the success page). Emitting here too
    // double-counts every purchase in PostHog funnels. GA4 + Google Ads
    // tags stay client-side because ad attribution needs the browser.

    this.trackConversion('PURCHASE', value, currency, transactionId, { skipPostHog: true });

    this.log('Purchase:', { tier, value, transactionId });
  }

  /**
   * Set Clarity tag for session filtering
   */
  setTag(key: string, value: string): void {
    if (!this.shouldTrack()) {
      this.log('Set Tag:', { key, value });
      return;
    }

    clarity.setTag(key, value);
  }

  /**
   * Reset user data (on logout)
   */
  reset(): void {
    this.user = null;

    // Sentry is error tracking — always clear user context
    clearSentryUser();

    if (!this.shouldTrack()) {
      this.log('Reset');
      return;
    }

    ga4.reset();
    clarity.reset();
    posthog.reset();

    this.log('Reset user data');
  }

  /**
   * Check if product-analytics tracking should fire.
   *
   * Returns false for internal/staff (superadmin) users so they never pollute
   * GA4 / Clarity / PostHog / Google Ads. Sentry is handled separately in
   * identify()/updateUser()/reset() (always set, since it is error tracking).
   */
  private shouldTrack(): boolean {
    if (this.user?.isSuperadmin) return false;
    return this.initialized || DEBUG_MODE;
  }

  /**
   * Log analytics activity in development/debug mode
   */
  private log(...args: unknown[]): void {
    if (!IS_PRODUCTION || DEBUG_MODE) {
      logInfo('[Analytics]', ...args);
    }
  }

  /**
   * Convert params to strings for Clarity
   */
  private paramsToStrings(
    params?: Record<string, string | number | boolean | undefined>
  ): Record<string, string> | undefined {
    if (!params) return undefined;

    const result: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        result[key] = String(value);
      }
    });
    return result;
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Re-export events and types for convenience
export { EVENTS } from './config';
export type { AnalyticsUser, TrackingEvent } from './types';

// Re-export PostHog feature flag functions for A/B testing
export { getFeatureFlag, isFeatureEnabled } from './posthog';

// Unified CTA click tracking (single funnel key: cta_location)
export { trackCtaClick } from './cta';
export type { CtaClickParams } from './cta';